import { useEffect } from "react"
import { rundeKaufmaennisch2Digits } from "../../models/calc/rundeKaufmaennisch"
import { Gesamtsummen } from "../../models/gesamtsummen"
import { Position } from "../../models/position"
import { SteuerDetails } from "../../models/rechnung/rechnungsebene/steuerdetails"
import { ZuAbschlag } from "../../models/rechnung/rechnungsebene/zuAbschlag"
import { get } from "../../Utils"

/**
 * Hook to calculate the following fields:
 * - BT-106
 * - BT-107
 * - BT-108
 * - BT-109
 * - BT-110
 * - BT-112
 * - BT-115
 *
 * @param {({
 *   positionen: Position[];
 *   rechnungAbschlaege: ZuAbschlag[];
 *   rechnungZuschlaege: ZuAbschlag[];
 *   gesamtsummen: Gesamtsummen;
 *   onChange: (path: string) => (value: number | null) => void;
 * })} {
 *   positionen,
 *   rechnungAbschlaege,
 *   rechnungZuschlaege,
 *   gesamtsummen,
 *   onChange,
 * }
 * @returns {({
 *     summePositionenNetto: number,
 *     nachlassNetto: number,
 *     zuschlagNetto: number,
 *     gesamtsummeNetto: number,
 *     summeUmsatzsteuer: number | null,
 *     gesamtsummeBrutto: number,
 *     faelligerBetrag: number,
 *   })}
 */
export const useGesamtsummen = ({
    steuerDetails,
    positionen,
    rechnungAbschlaege,
    rechnungZuschlaege,
    gesamtsummen,
    onChange,
}: {
    steuerDetails: SteuerDetails[]
    positionen: Position[]
    rechnungAbschlaege: ZuAbschlag[]
    rechnungZuschlaege: ZuAbschlag[]
    gesamtsummen: Gesamtsummen
    onChange: (path: string) => (value: number | null) => void
}): {
    summePositionenNetto: number
    nachlassNetto: number
    zuschlagNetto: number
    gesamtsummeNetto: number
    summeUmsatzsteuer: number | null
    gesamtsummeBrutto: number
    faelligerBetrag: number
} => {
    const summePositionenNetto = useSummePositionenNetto({
        onChange,
        positionen,
    })
    const nachlassNetto = useNachlassNetto({ onChange, rechnungAbschlaege })
    const zuschlagNetto = useZuschlagNetto({ onChange, rechnungZuschlaege })
    const gesamtsummeNetto = useGesamtSummeNetto({
        onChange,
        summePositionenNetto,
        nachlassNetto,
        zuschlagNetto,
    })
    const summeUmsatzsteuer = useSummeUmsatzsteuer({ onChange, steuerDetails, rechnungZuschlaege, rechnungAbschlaege })
    const gesamtsummeBrutto = useGesamtSummeBrutto({
        onChange,
        gesamtsummeNetto,
        summeUmsatzsteuer: summeUmsatzsteuer || 0,
    })
    const faelligerBetrag = useFaelligerBetrag({
        onChange,
        gesamtsummeBrutto,
        gesamtsummen,
    })

    return {
        summePositionenNetto,
        nachlassNetto,
        zuschlagNetto,
        gesamtsummeNetto,
        summeUmsatzsteuer,
        gesamtsummeBrutto,
        faelligerBetrag,
    }
}

const useNachlassNetto = ({
    onChange,
    rechnungAbschlaege,
}: {
    rechnungAbschlaege: ZuAbschlag[]
    onChange: (path: string) => (value: number | null) => void
}) => {
    const nachlassNetto = calculateSum(rechnungAbschlaege, "zuAbschlaege", "nachlassNetto")
    useEffect(() => {
        onChange("summeAbschlaegeNetto")(nachlassNetto)
    }, [onChange, nachlassNetto])

    return nachlassNetto
}

const useZuschlagNetto = ({
    onChange,
    rechnungZuschlaege,
}: {
    rechnungZuschlaege: ZuAbschlag[]
    onChange: (path: string) => (value: number | null) => void
}) => {
    const zuschlagNetto = calculateSum(rechnungZuschlaege, "zuAbschlaege", "nachlassNetto")

    useEffect(() => {
        onChange("summeZuschlaegeNetto")(zuschlagNetto)
    }, [onChange, zuschlagNetto])

    return zuschlagNetto
}

const useSummePositionenNetto = ({ onChange, positionen }: { positionen: Position[]; onChange: (path: string) => (value: number | null) => void }): number => {
    const summePositionenNetto = calculateSum(positionen, "gesamtpreisNetto")
    useEffect(() => {
        onChange("summePositionenNetto")(summePositionenNetto)
    }, [onChange, summePositionenNetto])

    return summePositionenNetto
}

const useGesamtSummeNetto = ({
    onChange,
    summePositionenNetto,
    nachlassNetto,
    zuschlagNetto,
}: {
    summePositionenNetto: number
    nachlassNetto: number
    zuschlagNetto: number
    onChange: (path: string) => (value: number | null) => void
}): number => {
    const gesamtsummeNetto = rundeKaufmaennisch2Digits((summePositionenNetto || 0) - Number(nachlassNetto || 0) + Number(zuschlagNetto || 0))

    useEffect(() => {
        onChange("gesamtsummeNetto")(gesamtsummeNetto)
    }, [onChange, gesamtsummeNetto])

    return gesamtsummeNetto
}

const useSummeUmsatzsteuer = ({
    steuerDetails,
    onChange,
}: {
    steuerDetails: SteuerDetails[]
    rechnungZuschlaege: ZuAbschlag[]
    rechnungAbschlaege: ZuAbschlag[]
    onChange: (path: string) => (value: number | null) => void
}): number | null => {
    const summeUmsatzsteuer = steuerDetails.reduce((sum, currentValue) => Number(sum + currentValue.umsatzsteuerbetrag), 0)

    const rounded = summeUmsatzsteuer == null ? null : rundeKaufmaennisch2Digits(summeUmsatzsteuer)

    useEffect(() => {
        onChange("summeUmsatzsteuer")(rounded)
    }, [onChange, rounded])

    return rounded
}

const useGesamtSummeBrutto = ({
    onChange,
    gesamtsummeNetto,
    summeUmsatzsteuer,
}: {
    gesamtsummeNetto: number
    summeUmsatzsteuer: number
    onChange: (path: string) => (value: number | null) => void
}): number => {
    const gesamtsummeBrutto = rundeKaufmaennisch2Digits(gesamtsummeNetto + Number(summeUmsatzsteuer || 0))

    useEffect(() => {
        onChange("gesamtsummeBrutto")(gesamtsummeBrutto)
    }, [onChange, gesamtsummeBrutto])

    return gesamtsummeBrutto
}

const useFaelligerBetrag = ({
    gesamtsummeBrutto,
    gesamtsummen,
    onChange,
}: {
    gesamtsummeBrutto: number
    gesamtsummen: Gesamtsummen
    onChange: (path: string) => (value: number | null) => void
}): number => {
    const faelligerBetrag = rundeKaufmaennisch2Digits(
        Number(gesamtsummeBrutto) - Number(gesamtsummen.gezahlterBetrag || 0) + Number(gesamtsummen.rundungsBetrag || 0),
    )

    useEffect(() => {
        onChange("faelligerBetrag")(faelligerBetrag)
    }, [onChange, faelligerBetrag])

    return faelligerBetrag
}

function calculateSum<Data, Key1 extends keyof NonNullable<Data>>(data: Data[], key1: Key1): number

function calculateSum<Data, Key1 extends keyof NonNullable<Data>, Key2 extends keyof NonNullable<NonNullable<Data>[Key1]>>(
    data: Data[],
    key1: Key1,
    key: Key2,
): number

function calculateSum(data: any[], ...keys: string[]) {
    return data.reduce((a, b) => Number((a + Number(keys[1] ? get(b, keys[0], keys[1]) : get(b, keys[0]) || 0)).toFixed(2)), 0) || null
}
